import { v4 as uuid } from 'uuid';
import { Framework, type IFramework, type IHistoryItem } from '@/framework';
import { setLayerRuntimeValue } from '@/utils/layer-kit';
import { LayerType, MaterialIdsType, type ILayerProps, type ITextLayerProps } from '@/version-types';
import {
    applySnapshotSize,
    collectFontSubsetRequests,
    measureLayer,
    pushHistory,
    refreshRuntimeFontFamilies,
    updateLayer,
} from '../utils';
import { getTextLayerHost } from '../utils/host';
import { type IShapeText } from './shape-text';

export type SpecificTextStyle = Pick<ITextLayerProps, 'fontFamily' | 'fontSize' | 'bold' | 'italic'>;
export type ReplaceLayerTextOptions = {
    resetLayerID?: boolean;
    update?: boolean;
    triggerFuncID?: string;
    fixedWidth?: boolean;
};

export interface IReplacer {
    /**
     * 批量替换文本图层
     * @param prevLayers - 旧图层数组
     * @param nextLayers - 新图层数组
     * @param historyFlag - 历史记录标志
     */
    replaceLayers(prevLayers: ILayerProps[], nextLayers: ILayerProps[], historyFlag?: string): void;

    /**
     * 根据新的文本内容及原始图层数据，创建新的文本图层
     * @param layer - 原始文本图层
     * @param text - 新的文本内容
     * @param options - 配置选项
     *  `options.resetLayerID` - 是否重置图层 ID，默认为 false
     *  `options.update` - 是否需要更新舞台，默认为 false
     *  `options.triggerFuncID` - 触发该功能的功能 ID，默认为空
     * @returns - 新的文本图层
     */
    replaceLayerText(layer: ILayerProps, text: string, options?: ReplaceLayerTextOptions): Promise<ILayerProps | void>;

    /**
     * 根据指定的样式信息更新文本图层
     * @param layerID - 图层 ID
     * @param style - 部分图层数据
     * @param fontMaterialID - 字体素材 ID
     * @returns - 更新后的文本图层
     */
    updateBySpecificStyle(
        layerID: string,
        style: SpecificTextStyle,
        fontMaterialID?: number,
    ): Promise<ILayerProps | void>;
}

const HISTORY_DATA_KEY = 'REPLACE_TEXT_LAYERS';

type HistoryPayload = {
    prevLayers: ILayerProps[];
    nextLayers: ILayerProps[];
};

export class ReplaceTextLayerHistory extends Framework {
    constructor(identity: string) {
        super(identity);

        this.framework.service.history.register(HISTORY_DATA_KEY, {
            owner: 'layer/text',
            collaboration: 'supported',
            onRedoUndo: (historyItem: IHistoryItem<HistoryPayload>, isRedo: boolean, isUndo: boolean) => {
                if (historyItem.isDirty) return;
                const data = historyItem.data.data;
                if (isUndo) {
                    const { prevLayers } = data;
                    prevLayers.forEach((layer) => {
                        this.framework.service.frame.addOrUpdateLayer(layer);
                    });
                }
                if (isRedo) {
                    const { nextLayers } = data;
                    nextLayers.forEach((layer) => {
                        this.framework.service.frame.addOrUpdateLayer(layer);
                    });
                }
            },
            onAfterWrite: (historyItem: IHistoryItem<HistoryPayload>) => {
                return {
                    message: [historyItem.data.data.nextLayers],
                };
            },
            onAfterRedoUndo(historyItem: IHistoryItem<HistoryPayload>, isRedo) {
                return {
                    message: [isRedo ? historyItem.data.data.nextLayers : historyItem.data.data.prevLayers],
                };
            },
            onBeforeRedoUndo: (historyItem: IHistoryItem<HistoryPayload>, isRedo, isUndo, store) => {
                if (isUndo) {
                    const { nextLayers } = historyItem.data.data;
                    for (const originLayer of nextLayers) {
                        historyItem.isDirty = this.framework.service.flatLayer.checkLayerIsDirty({
                            originLayer,
                            latestIsExtraGroup: this.framework.service.flatLayer.checkIsExtraGroup(originLayer),
                            latestIsFormalGroup: this.framework.service.flatLayer.checkIsFormalGroup(originLayer),
                        });
                        if (historyItem.isDirty) break;
                    }
                    store.set(historyItem.cursor, {
                        isDirty: historyItem.isDirty,
                    });
                }
                if (isRedo) {
                    const beforeData = store.get(historyItem.cursor);
                    if (!beforeData) {
                        historyItem.isDirty = true;
                        return;
                    }
                    historyItem.isDirty = beforeData.isDirty;
                    if (!historyItem.isDirty) {
                        const { prevLayers } = historyItem.data.data;
                        for (const originLayer of prevLayers) {
                            historyItem.isDirty = this.framework.service.flatLayer.checkLayerIsDirty({
                                originLayer,
                                latestIsExtraGroup: this.framework.service.flatLayer.checkIsExtraGroup(originLayer),
                                latestIsFormalGroup: this.framework.service.flatLayer.checkIsFormalGroup(originLayer),
                            });
                            if (historyItem.isDirty) break;
                        }
                    }
                }
            },
            onMessage: (message: ILayerProps[]) => {
                this.framework.service.frame.delActiveLayer();
                message.forEach((item) => {
                    this.framework.service.frame.addOrUpdateLayer(item);
                });
            },
        });
    }
}

export class Replacer implements IReplacer {
    constructor(
        private framework: IFramework,
        private shapeText: IShapeText,
    ) {}

    private async prepareFontSubsets(layer: ILayerProps): Promise<boolean> {
        const requests = collectFontSubsetRequests(layer);
        if (requests.length === 0) return true;

        const results = await Promise.all(
            requests.map(async ({ fontFamily, chars }) => {
                if (!this.framework.service.font.getByFamily(fontFamily)) return false;
                const subsetFamily = await this.framework.service.font.loadSubset(fontFamily, chars);
                if (!subsetFamily) return false;

                setLayerRuntimeValue(layer, {
                    fontFamilyMap: {
                        ...(layer.__runtime?.fontFamilyMap || {}),
                        [fontFamily]: subsetFamily,
                    },
                });
                return true;
            }),
        );

        return results.every(Boolean);
    }

    replaceLayers(prevLayers: ILayerProps[], nextLayers: ILayerProps[], historyFlag?: string) {
        this.framework.service.history.write({
            type: HISTORY_DATA_KEY,
            data: {
                prevLayers,
                nextLayers,
                historyFlag,
            },
            originIndex: prevLayers?.[0]?.__originIndex,
        });
    }

    async replaceLayerText(layer: ILayerProps, text: string, options: ReplaceLayerTextOptions = {}) {
        if (layer.layerType !== LayerType.TEXT_LAYER) return;

        const { resetLayerID = false, update = false, triggerFuncID, fixedWidth = false } = options;

        const prevLayerData = layer.data as ITextLayerProps;
        const nextLayerData: ITextLayerProps = {
            ...prevLayerData,
            text,
            textContents: undefined,
        };

        let nextLayer: ILayerProps = {
            ...layer,
            data: nextLayerData,
            __fixedWidth: fixedWidth,
        };

        if (resetLayerID) nextLayer = { ...nextLayer, id: uuid() };

        const { shapeExtension } = prevLayerData;
        if (!shapeExtension) {
            nextLayer = refreshRuntimeFontFamilies(nextLayer);
            if (!(await this.prepareFontSubsets(nextLayer))) return;

            const engine = this.framework.service.textEngine.getEngine();
            if (!engine || engine.isDisposed) return;

            const textSize = measureLayer(engine, nextLayer);
            nextLayer = { ...nextLayer, width: textSize.width, height: textSize.height };
        } else {
            const refreshedLayer = await this.shapeText.refreshShapeTextRender(nextLayer);
            if (refreshedLayer) nextLayer = refreshedLayer;
        }

        if (update) {
            this.framework.service.frame.addOrUpdateLayer(nextLayer);
            this.framework.service.frame.setActiveLayer(nextLayer.id);
            this.framework.service.history.write(layer, nextLayer, triggerFuncID);
        }

        return nextLayer;
    }

    /**
     * 更新指定文字样式，并由真实 Text Layer 宿主同步最终几何。
     *
     * @param layerID - 目标 Text Layer ID。
     * @param style - 需要覆盖的文字样式。
     * @param fontMaterialID - 可选的字体素材 ID。
     * @returns 更新后的图层；输入、字体或 renderer 不可用时返回 void。
     */
    async updateBySpecificStyle(layerID: string, style: SpecificTextStyle, fontMaterialID?: number) {
        const prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!prevLayer || prevLayer.layerType !== LayerType.TEXT_LAYER) return;

        const prevLayerData = prevLayer.data as ITextLayerProps;
        const prevTextContents = prevLayerData.textContents || [];
        const nextTextContents = prevTextContents.map((item) => ({ ...item, ...style })); // 分段内容也同时更新

        const nextLayerData: ITextLayerProps = {
            ...prevLayerData,
            ...style,
            textContents: nextTextContents,
        };
        let nextLayer: ILayerProps = {
            ...prevLayer,
            data: nextLayerData,
        };

        const { shapeExtension } = prevLayerData;
        if (!shapeExtension) {
            nextLayer = refreshRuntimeFontFamilies(nextLayer);
            if (!(await this.prepareFontSubsets(nextLayer))) return;

            const resolution = getTextLayerHost(this.framework.service.edgelessCanvas, nextLayer.id)?.layout(nextLayer);
            // 替换后的文字属性和尺寸只能消费当前 ready 结果。
            if (resolution?.status !== 'ready') return;
            const snapshot = resolution.value;

            nextLayer = applySnapshotSize(nextLayer, snapshot);
        } else {
            const refreshedLayer = await this.shapeText.refreshShapeTextRender(nextLayer);
            if (refreshedLayer) nextLayer = refreshedLayer;
        }

        if (fontMaterialID && nextLayer.materialIds) {
            nextLayer = {
                ...nextLayer,
                materialIds: {
                    ...nextLayer.materialIds,
                    [MaterialIdsType.FONT_FAMILY]: [String(fontMaterialID)],
                },
            };
        }

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer);

        return nextLayer;
    }
}
